'use client';

import { useEffect, useState } from 'react';

// Slide-in keyframe duration (`--animate-trail-row-in` in styles/globals.css).
// Phase 1 of the post-finish state machine waits this long so the final row's
// entry animation finishes: panel mode drops the keyframe class from the rows.
export const SLIDE_IN_DURATION_MS = 600;

// Settle window between panel mode turning on (toggle and chevron appear) and
// the collapse trigger. Sized so the user briefly sees the finished trail
// expanded before it animates closed — without it the collapse feels abrupt.
// Total dwell from slide-in start to collapse-start =
// SLIDE_IN_DURATION_MS + SETTLE_BEFORE_COLLAPSE_MS.
export const SETTLE_BEFORE_COLLAPSE_MS = 400;

// Back-compat alias: callers that imported the single legacy delay still
// resolve to the slide-in + settle total.
export const AUTO_COLLAPSE_DELAY_MS = SLIDE_IN_DURATION_MS + SETTLE_BEFORE_COLLAPSE_MS;

export interface TrailCollapseMode {
  /** True once the trail can fold (history view or post-finish): toggle and chevron show. */
  renderPanelMode: boolean;
  /** Effective expanded state — controlled prop wins over internal toggle. */
  expanded: boolean;
  /** True after at least one render under `isLive=true`. Read by the motion
   *  wrapper on mount to decide between an animated entry (live → finished
   *  swap) and an instant mount (history view). */
  wasEverLive: boolean;
  /** Flips the user-driven expansion when in panel mode. */
  toggle: () => void;
}

/**
 * Drives the activity-trail's collapse state machine:
 *
 *  • Mounts as live → flat mode, expanded.
 *  • Mounts as not-live (history) → panel mode, collapsed, never-was-live.
 *  • Live → not-live transition runs in two phases so the collapse animates
 *    smoothly instead of snapping:
 *      ① wait `SLIDE_IN_DURATION_MS` while the new final row slides in,
 *         then turn on panel mode while keeping `expanded=true`.
 *      ② wait `SETTLE_BEFORE_COLLAPSE_MS` more, then flip `expanded=false`,
 *         which folds the earlier rows (`1fr → 0fr`).
 *  • Not-live → live transition (regenerate) → resets to flat mode expanded.
 *  • `controlledExpanded` (storybook) overrides the internal toggle and
 *    forces panel-mode rendering so the chevron + collapse are demoable.
 *  • Once the user has manually toggled (or auto-collapse has fired), the
 *    timers don't re-arm: a manual expand stays expanded.
 *
 * Resets on live-resumption are done via the React "track previous value
 * during render" pattern (not an effect) so the next paint already shows
 * flat mode — no panel-mode flash for one frame.
 */
export function useTrailCollapseMode({
  isLive,
  controlledExpanded,
}: {
  isLive: boolean;
  controlledExpanded: boolean | undefined;
}): TrailCollapseMode {
  const [usePanelMode, setUsePanelMode] = useState(!isLive);
  const [userExpanded, setUserExpanded] = useState(isLive);
  const [wasEverLive, setWasEverLive] = useState(isLive);
  const [trackedIsLive, setTrackedIsLive] = useState(isLive);
  // Lock the auto-collapse so it fires at most once per live → not-live
  // transition. Without this, a manual re-expand after auto-collapse would
  // immediately re-trigger the collapse from the still-armed effect.
  const [didAutoCollapse, setDidAutoCollapse] = useState(!isLive);

  if (trackedIsLive !== isLive) {
    setTrackedIsLive(isLive);
    if (isLive) {
      setWasEverLive(true);
      setUsePanelMode(false);
      setUserExpanded(true);
      setDidAutoCollapse(false);
    }
  }

  // Two-phase auto-collapse, chained via nested setTimeout so the second
  // phase doesn't have to wait on a React re-render + effect re-run between
  // panel mode turning on and the collapse trigger.
  //
  //   ① after `SLIDE_IN_DURATION_MS`: turn on panel mode, keep expanded=true.
  //   ② after a further `SETTLE_BEFORE_COLLAPSE_MS`: flip expanded=false.
  //     The earlier rows transition `1fr → 0fr` and fold.
  useEffect(() => {
    if (isLive || controlledExpanded !== undefined || didAutoCollapse) return;
    let phase2Id: ReturnType<typeof setTimeout> | undefined;
    const phase1Id = setTimeout(() => {
      setUsePanelMode(true);
      phase2Id = setTimeout(() => {
        setUserExpanded(false);
        setDidAutoCollapse(true);
      }, SETTLE_BEFORE_COLLAPSE_MS);
    }, SLIDE_IN_DURATION_MS);
    return () => {
      clearTimeout(phase1Id);
      if (phase2Id !== undefined) clearTimeout(phase2Id);
    };
  }, [isLive, controlledExpanded, didAutoCollapse]);

  return {
    renderPanelMode: usePanelMode || controlledExpanded !== undefined,
    expanded: controlledExpanded ?? userExpanded,
    wasEverLive,
    toggle: () => {
      // A manual toggle counts as user intent — freeze the auto-collapse
      // timeline so we don't yank the panel closed underneath them.
      setDidAutoCollapse(true);
      setUserExpanded(prev => !prev);
    },
  };
}
